/**
 * Guarded service worker registration for the NutriMarket app shell.
 *
 * Rules (see Lovable PWA skill):
 * - Never register in dev, iframe, or any Lovable preview host.
 * - Support `?sw=off` as a kill switch.
 * - Only manage `/sw.js` here. Do NOT touch `/push-sw.js` (messaging worker,
 *   registered separately on user opt-in).
 */

import { isLegacyPublicDomain } from "@/lib/primaryDomain";

const APP_SW_URL = "/sw.js";

function isLovablePreviewHost(host: string): boolean {
  return (
    host.startsWith("id-preview--") ||
    host.startsWith("preview--") ||
    host === "lovableproject.com" ||
    host.endsWith(".lovableproject.com") ||
    host === "lovableproject-dev.com" ||
    host.endsWith(".lovableproject-dev.com") ||
    host === "beta.lovable.dev" ||
    host.endsWith(".beta.lovable.dev")
  );
}

async function unregisterAppSW(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs
        .filter((r) => {
          const scriptURL = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || "";
          // Only unregister our app shell SW. Leave push-sw.js alone.
          return scriptURL.endsWith(APP_SW_URL);
        })
        .map((r) => r.unregister()),
    );
  } catch {
    /* no-op */
  }
}

export function registerAppServiceWorker(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  const inIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  const url = new URL(window.location.href);
  const killSwitch = url.searchParams.get("sw") === "off";
  const host = window.location.hostname;

  const refuse =
    !import.meta.env.PROD ||
    inIframe ||
    killSwitch ||
    isLegacyPublicDomain(host) ||
    isLovablePreviewHost(host);

  if (refuse) {
    void unregisterAppSW();
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker.register(APP_SW_URL, { scope: "/" }).catch(() => {
      /* no-op: SW failures must never break the app */
    });
  });
}